import { createContext, useState, useMemo, useCallback } from 'react'
import moment from 'moment'
import { node } from 'prop-types'
import { api } from 'api'
import { exponentialBackoff } from 'functions'

export const reportContext = createContext({})

export const ReportProvider = ({ children }) => {
  const [submissions, setSubmissions] = useState([])
  const [topUsers, setTopUsers] = useState({})
  const [templates, setTemplates] = useState([])
  const defaultStartDate = '2010-01-01'

  const today = useMemo(() => moment().add(1, 'day').format('YYYY-MM-DD'), [])

  const reportTypes = useMemo(
    () => ({
      usage: async (token, range) => {
        await api.get(
          `/api/v1/account/reports/usage-report/${token}/${
            range ? range.startDate : defaultStartDate
          }/${range ? range.endDate : today}`,
        )

        window.location = `/api/v1/account/reports/users-report/${token}`
      },

      logins: token => {
        window.location = `/api/v1/account/reports/${token}/login-activity`
      },

      submissions: async (token, templateId) => {
        const resp = await api.get(`/api/v1/account/reports/${templateId}/csv`)

        exponentialBackoff(
          async () => {
            const { data } = await api.get(`/api/v1/account/reports/${resp.data}/status`)

            if (window.location && data?.location) {
              window.location = data.location
            }
            return data
          },
          res => res.status === 'successful' || res.status === 'error',
        )
      },
    }),
    [today, defaultStartDate],
  )

  const fetchSubmissionsReport = useCallback(
    (startDate, endDate) =>
      api
        .post('/api/v1/account/reports/usage', {
          startDate: startDate || defaultStartDate,
          endDate: endDate || today,
        })
        .then(({ data }) => {
          setSubmissions(data.usage)
          return data
        }),
    [today, defaultStartDate],
  )

  const downloadReport = useCallback(
    async (type, params) => {
      const { data } = await api.get(`/api/v1/account/reports/csv/token/global`)
      return reportTypes[type](data.token, params)
    },
    [reportTypes],
  )

  const fetchTopUsersReport = useCallback(async () => {
    const { data } = await api.get('/api/v1/account/reports/topusers')
    setTopUsers(data.topUsers)
  }, [])

  const fetchTemplateReports = useCallback(
    () =>
      api.get('/api/core/v1/reports/templates').then(({ data }) => {
        setTemplates(data)
        return data
      }),
    [],
  )

  const value = useMemo(
    () => ({
      submissions,
      fetchSubmissionsReport,
      topUsers,
      fetchTopUsersReport,
      downloadReport,
      fetchTemplateReports,
      templates,
    }),
    [submissions, topUsers, downloadReport, templates],
  )

  return <reportContext.Provider value={value}>{children}</reportContext.Provider>
}

ReportProvider.propTypes = {
  children: node.isRequired,
}
